import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "tarah-chat-session";

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const WELCOME =
  "Hello! I'm Tarah's cake assistant. Tell me about your occasion — who it's for, how many guests, colours or a theme — and I'll help you shape a cake idea and give you a price guide.";

const STARTERS = [
  "Help me design a birthday cake",
  "What flavours and fillings do you offer?",
  "How much is a cake for 20 people?",
  "Do you list allergens?",
];

/** Minimal markdown: **bold**, bullet lines, paragraphs. */
const renderText = (text) =>
  text.split(/\n{2,}/).map((para, pi) => {
    const lines = para.split("\n");
    const isList = lines.every((l) => /^\s*([-•*]|\d+\.)\s+/.test(l));
    const inline = (str) =>
      str.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") ? (
          <strong key={i} className="font-semibold text-[#1B3A6B]">
            {part.slice(2, -2)}
          </strong>
        ) : (
          part
        ),
      );
    if (isList) {
      return (
        <ul key={pi} className="my-1.5 list-disc space-y-1 pl-4">
          {lines.map((l, i) => (
            <li key={i}>{inline(l.replace(/^\s*([-•*]|\d+\.)\s+/, ""))}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={pi} className="my-1.5">
        {lines.map((l, i) => (
          <span key={i}>
            {inline(l)}
            {i < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
  });

const Bubble = ({ m, i }) => (
  <div
    data-testid={`chat-message-${m.role}-${i}`}
    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
  >
    <div
      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        m.role === "user"
          ? "rounded-br-sm bg-[#1B3A6B] text-white"
          : "rounded-bl-sm border border-[#D4AF37]/25 bg-white text-[#2F4A78]"
      }`}
    >
      {m.role === "user" ? m.content : renderText(m.content || "…")}
    </div>
  </div>
);

export const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    const fallback = [{ role: "assistant", content: WELCOME }];
    fetch(`${API}/chat/${getSessionId()}`)
      .then((r) => r.json())
      .then((h) =>
        setMessages((prev) => (prev.length ? prev : h.length ? h : fallback)),
      )
      .catch(() => setMessages((prev) => (prev.length ? prev : fallback)))
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: msg },
      { role: "assistant", content: "" },
    ]);
    const append = (chunk) =>
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          ...next[next.length - 1],
          content: next[next.length - 1].content + chunk,
        };
        return next;
      });
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          const type = ev.match(/^event: (.+)$/m)?.[1];
          const data = ev.match(/^data: (.+)$/m)?.[1];
          if (!type || !data) continue;
          const payload = JSON.parse(data);
          if (type === "delta") append(payload.text);
          if (type === "error") append(payload.message);
        }
      }
    } catch {
      append(
        "Sorry, I couldn't reply just now. Please try again or send an enquiry below.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-4 z-[70] flex h-[min(600px,calc(100vh-8rem))] w-[min(400px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#FDFBF7] shadow-2xl shadow-[#1B3A6B]/20"
          >
            <div className="flex items-center justify-between border-b border-[#D4AF37]/20 bg-white px-5 py-4">
              <div>
                <p className="font-label text-[10px] tracking-[0.3em] text-[#9A7B24]">
                  CAKE ASSISTANT
                </p>
                <p className="font-display text-lg text-[#1B3A6B]">
                  Design your cake with Tarah
                </p>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="rounded-full p-2 text-[#67718A] transition-colors hover:bg-[#F7F2E8] hover:text-[#1B3A6B]"
              >
                <X size={18} />
              </button>
            </div>

            <div
              ref={listRef}
              className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
              data-testid="chat-messages"
            >
              {messages.map((m, i) => (
                <Bubble key={i} m={m} i={i} />
              ))}
              {messages.length <= 1 && !busy && (
                <div
                  className="flex flex-wrap gap-2 pt-2"
                  data-testid="chat-starters"
                >
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-starter-${s
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .slice(0, 30)}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-[#D4AF37]/40 bg-white px-3 py-1.5 text-xs text-[#8A6D1F] transition-colors hover:bg-[#D4AF37] hover:text-[#1B3A6B]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-[#D4AF37]/20 bg-white px-3 py-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Describe your cake idea…"
                maxLength={1500}
                className="flex-1 rounded-full border border-[#D4AF37]/25 bg-[#FDFBF7] px-4 py-2.5 text-sm text-[#1B3A6B] placeholder:text-[#9AA3B8] focus:border-[#D4AF37] focus:outline-none"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Send"
                className="rounded-full bg-[#D4AF37] p-2.5 text-[#1B3A6B] transition-colors hover:bg-[#8A6D1F] hover:text-white disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={open ? "Close cake assistant" : "Open cake assistant"}
        className="fixed bottom-6 right-4 z-[70] flex items-center gap-2 rounded-full bg-[#1B3A6B] px-5 py-3.5 text-sm font-medium text-white shadow-xl shadow-[#1B3A6B]/30 transition-colors hover:bg-[#8A6D1F]"
      >
        {open ? <X size={18} /> : <MessageCircle size={18} />}
        <span className="hidden sm:inline">
          {open ? "Close" : "Design your cake"}
        </span>
        {!open && <Sparkles size={14} className="text-[#D4AF37]" />}
      </motion.button>
    </>
  );
};
